import { useCallback, useRef, useState, type ReactNode } from "react";
import { cancelProjectTestRun, getProjectTestRun, type QaJob, type QaJobRun, type QaTestRun } from "../api/qaOrchestrator";
import { useConfirm } from "./ConfirmDialog";
import { OFFLINE_AFTER_MS, QaPlanRun } from "./QaRunDetail";
import { CountChips, OPEN, runDuration, runTitle } from "./QaTestRuns";
import { relativeTime } from "./admin/memberModel";
import { describeTarget } from "../lib/qaTarget";
import { usePolling } from "../hooks/usePolling";
import "./admin/qa-orchestrator.css";
import "./admin/qa-run.css";

type Props = {
  project: string;
  projectName: string;
  runId: string;
  onBack: () => void;
  /** Opened from a share link without project access: no way back, no cancel. */
  readOnly?: boolean;
};

const BAD = new Set(["fail", "error"]);

/** Every plan run of the test run, one per job and computer, for the side panel. */
type PlanItem = { job: QaJob; run: QaJobRun | null; key: string };
function planRuns(t: QaTestRun): PlanItem[] {
  return t.jobs.flatMap((job): PlanItem[] => (job.runs.length === 0 ? [{ job, run: null, key: job.id }] : job.runs.map((run) => ({ job, run, key: run.id }))));
}

/** The plan to show first: a failing one, else the one running, else the first. */
function firstPick(items: ReturnType<typeof planRuns>): string | null {
  return (items.find((i) => i.run && BAD.has(i.run.status)) ?? items.find((i) => i.run?.status === "running") ?? items[0])?.key ?? null;
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </>
  );
}

/** One test run on its own page: a side panel with what it is and its plans, the chosen plan's run beside it. */
export default function QaRunPage({ project, projectName, runId, onBack, readOnly = false }: Props) {
  const [run, setRun] = useState<QaTestRun | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [picked, setPicked] = useState<string | null>(null);
  const [confirm, dialog] = useConfirm();
  const [cancelling, setCancelling] = useState(false);

  // A failed refresh keeps the run already shown: the error appears only when
  // nothing loaded yet, or refreshes kept failing for OFFLINE_AFTER_MS.
  const failingSince = useRef<number | null>(null);
  const loaded = useRef(false);
  const load = useCallback(() => getProjectTestRun(project, runId).then((r) => { setRun(r); setError(null); loaded.current = true; failingSince.current = null; }).catch((e) => {
    failingSince.current ??= Date.now();
    if (!loaded.current || Date.now() - failingSince.current >= OFFLINE_AFTER_MS) setError((e as Error).message);
  }), [project, runId]);
  const open = run ? OPEN.has(run.status) : true;
  // Followed while it runs; once it has finished, read once more and left alone.
  usePolling(load, open ? 5_000 : null);

  const items = run ? planRuns(run) : [];
  const selected = items.find((i) => i.key === picked) ?? items.find((i) => i.key === firstPick(items)) ?? null;
  const trigger = run?.trigger;

  const cancel = async () => {
    const ok = await confirm({
      title: "Cancel this test run?",
      body: <p>The plan running now stops before its next step, and the plans still waiting are dropped. What already ran stays here, recording included.</p>,
      confirmLabel: "Cancel run",
      cancelLabel: "Keep running",
      danger: true,
    });
    if (!ok) return;
    setCancelling(true);
    try { await cancelProjectTestRun(project, runId); await load(); } catch (e) { setError((e as Error).message); } finally { setCancelling(false); }
  };

  return (
    <div className="qrp">
      <header className="qa-page-header qrp-header">
        {!readOnly && <button type="button" className="qa-page-back" onClick={onBack} aria-label="Back to the test runs">←</button>}
        <div className="qrp-title">
          <div className="qo-muted qrp-crumb">QA tests · {projectName} · test run</div>
          <h1>
            {run && <span className={`qo-pill qo-pill-${run.status}`}>{open && <span className="qr-live-dot" />}{run.status}</span>}
            {run ? runTitle(run) : "Loading…"}
          </h1>
        </div>
        {run && <CountChips counts={run.counts} />}
      </header>
      {error && <div className="qo-error-text" role="alert">{error}</div>}

      {run && (
        <div className="qrp-layout">
          <aside className="qrp-side">
            <section className="qrp-card">
              <h2>Summary</h2>
              <dl className="qrp-facts">
                <Row label="Commit">
                  <code>{run.sha.slice(0, 8)}</code>
                  {run.ref !== run.sha && <span className="qo-muted"> on {run.ref}</span>}
                </Row>
                <Row label="Repository">{run.repo}</Row>
                <Row label="Started">{relativeTime(run.createdAt)}{trigger?.actor ? ` by ${trigger.actor}` : ""}</Row>
                {runDuration(run) && <Row label="Duration">{runDuration(run)}</Row>}
                <Row label="Trigger">{trigger ? trigger.event.replace(/_/g, " ") : "manual"}</Row>
                <Row label="Runs on">{describeTarget(run.target)}</Row>
                <Row label="Build">{run.packs.length > 0 ? "the PR's own build" : "the runners' pack versions"}</Row>
                {run.kind === "capture" && <Row label="Kind">re-baseline</Row>}
              </dl>
              {(trigger?.url || trigger?.runUrl) && (
                <div className="qrp-links">
                  {trigger.url && <a href={trigger.url} target="_blank" rel="noreferrer">{trigger.prNumber ? `Pull request #${trigger.prNumber}` : "Commit"} ↗</a>}
                  {trigger.runUrl && <a href={trigger.runUrl} target="_blank" rel="noreferrer">Workflow run ↗</a>}
                </div>
              )}
              {open && !readOnly && (
                <button className="btn btn-danger qrp-cancel" disabled={cancelling} onClick={() => void cancel()}>{cancelling ? "Cancelling…" : "Cancel run"}</button>
              )}
            </section>

            <section className="qrp-card">
              <h2>Plans <span className="qo-muted">{items.length}</span></h2>
              <nav className="qrp-plans" aria-label="Plans">
                {items.map((i) => {
                  const status = i.run?.status ?? i.job.status;
                  return (
                    <button key={i.key} className={`qrp-plan${selected?.key === i.key ? " on" : ""}`} onClick={() => setPicked(i.key)} aria-current={selected?.key === i.key ? "true" : undefined}>
                      <span className={`qrp-dot qrp-dot-${status}`} aria-hidden="true" />
                      <span className="qrp-plan-name">{i.job.plan ?? i.job.id}</span>
                      <span className="qo-muted qrp-plan-meta">{i.run ? (i.run.computerName ? `${status} · ${i.run.computerName}` : status) : "waiting for a runner"}</span>
                    </button>
                  );
                })}
              </nav>
            </section>
          </aside>

          <main className="qrp-main">
            {!selected ? (
              <div className="qo-muted">This run has no plans.</div>
            ) : selected.run ? (
              <QaPlanRun key={selected.key} project={project} testRun={run.id} job={selected.job} run={selected.run} />
            ) : (
              <div className="qr-run">
                <div className="qr-run-head"><span className={`qo-pill qo-pill-${selected.job.status}`}>{selected.job.status}</span><strong>{selected.job.plan ?? selected.job.id}</strong></div>
                <div className="qo-muted">Waiting for a runner. A runner starts it once its desktop has had no keyboard or mouse input for a while.</div>
              </div>
            )}
          </main>
        </div>
      )}
      {dialog}
    </div>
  );
}
